import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, ArrowLeft, ShieldOff } from "lucide-react";
import { CompassLogo } from "../components/CompassLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../lib/auth";
import { isAdminHost } from "../lib/host";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SUPPORT_EMAIL = "contact@squareandcompass.store";
const SUPPORT_PHONE = "+40751364236";

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { user, login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const adminHost = isAdminHost();
  const [showSupport, setShowSupport] = useState(false);
  // Step 1
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Step 2 (mfa or password change)
  const [step, setStep] = useState("credentials"); // credentials | mfa
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const codeInputRef = useRef(null);
  const [showSelfMfaReset, setShowSelfMfaReset] = useState(false);
  const [selfMfaPwd, setSelfMfaPwd] = useState("");
  const [selfMfaSubmitting, setSelfMfaSubmitting] = useState(false);

  const submitSelfMfaReset = async (e) => {
    e.preventDefault();
    setSelfMfaSubmitting(true);
    try {
      await api.post("/auth/superadmin/self-mfa-reset", { env_password: selfMfaPwd });
      toast.success(t("login.mfa_wiped_sign_in_with"),
      );
      setShowSelfMfaReset(false);
      setSelfMfaPwd("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSelfMfaSubmitting(false);
    }
  };

  useEffect(() => {
    // Auto-focus the 6-digit field when entering MFA step.
    if (step === "mfa" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  if (user && user !== false) {
    return <Navigate to={user.role === "superadmin" ? "/superadmin" : "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(username, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || t("login.error"));
      return;
    }
    // Two possible step-up outcomes from the backend:
    if (res.passwordChangeRequired && res.mfaToken) {
      // Forced password change before any access — redirect to dedicated UI.
      navigate(`/first-login?token=${encodeURIComponent(res.mfaToken)}&u=${encodeURIComponent(username)}`, { replace: true });
      return;
    }
    if (res.mfaRequired && res.mfaToken) {
      setMfaToken(res.mfaToken);
      setStep("mfa");
      setError("");
      return;
    }
    const role = res.user?.role;
    navigate(role === "superadmin" ? "/superadmin" : "/", { replace: true });
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await completeMfa(mfaToken, mfaCode, useBackupCode);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || t("login.invalid_mfa_code"));
      setMfaCode("");
      if (codeInputRef.current) codeInputRef.current.focus();
      return;
    }
    const role = res.user?.role;
    navigate(role === "superadmin" ? "/superadmin" : "/", { replace: true });
  };

  const cancelMfa = () => {
    setStep("credentials");
    setMfaToken("");
    setMfaCode("");
    setError("");
    setPassword("");
  };

  return (
    <div className="auth-bg flex items-center justify-center px-4">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      <form
        onSubmit={step === "mfa" ? handleMfaSubmit : handleSubmit}
        className="gold-card w-full max-w-md relative z-10 px-10 py-12"
        data-testid="login-form"
      >
        <div className="flex flex-col items-center text-center">
          <CompassLogo size={108} variant="dark" />
          <h1
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "1.35rem",
              letterSpacing: "0.18em",
              color: "#0d1b2a",
              marginTop: "1.1rem",
              whiteSpace: "nowrap",
            }}
            data-testid="login-brand-title"
          >
            SQUARE &amp; COMPASS
          </h1>
          <p
            style={{
              fontFamily: "Crimson Text, serif",
              fontStyle: "italic",
              color: adminHost ? "#9a7530" : "#858481",
              marginTop: "0.3rem",
              fontSize: "0.95rem",
              fontWeight: adminHost ? 600 : 400,
              letterSpacing: adminHost ? "0.1em" : 0,
            }}
          >
            {adminHost
              ? t("login.super_admin_portal")
              : "Lodge Management System"}
          </p>
        </div>

        <div className="gold-divider my-8" />

        {step === "credentials" ? (
          <div className="space-y-5">
            <div>
              <label className="label-lodge" htmlFor="username">
                {t("login.username")}
              </label>
              <input
                id="username"
                type="text"
                className="input-lodge"
                placeholder={t("login.username")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                data-testid="login-username-input"
              />
            </div>

            <div>
              <label className="label-lodge" htmlFor="password">
                {t("login.password")}
              </label>
              <input
                id="password"
                type="password"
                className="input-lodge"
                placeholder={t("login.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="error-banner" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary-gold w-full mt-2"
              data-testid="login-submit-btn"
            >
              {submitting ? "..." : t("login.title")}
            </button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="mfa-step">
            <div>
              <div className="label-lodge" style={{ marginBottom: "0.4rem" }}>
                {t("login.two_factor_authentication")}
              </div>
              <div style={{ fontFamily: "Crimson Text, serif", color: "#34322d", fontSize: "0.92rem", lineHeight: 1.45 }}>
                {useBackupCode
                  ? t("login.enter_one_of_your_backup")
                  : t("login.open_google_authenticator_and_enter")}
              </div>
            </div>

            <div>
              <label className="label-lodge" htmlFor="mfa-code">
                {useBackupCode
                  ? t("login.backup_code")
                  : t("login.authenticator_code")}
              </label>
              <input
                id="mfa-code"
                ref={codeInputRef}
                type="text"
                inputMode={useBackupCode ? "text" : "numeric"}
                className="input-lodge"
                placeholder={useBackupCode ? "ABCD-EFGH" : "123 456"}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoComplete="one-time-code"
                required
                maxLength={useBackupCode ? 12 : 7}
                data-testid="login-mfa-input"
                style={{ letterSpacing: useBackupCode ? "0.2em" : "0.4em", textAlign: "center", fontSize: "1.3rem", fontFamily: "JetBrains Mono, monospace" }}
              />
            </div>

            {error && (
              <div className="error-banner" data-testid="login-mfa-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !mfaCode.trim()}
              className="btn-primary-gold w-full mt-2"
              data-testid="login-mfa-submit"
            >
              {submitting ? "..." : t("login.verify")}
            </button>

            <div className="flex items-center justify-between" style={{ fontSize: "0.78rem" }}>
              <button
                type="button"
                onClick={cancelMfa}
                style={{ background: "transparent", border: 0, color: "#9a7530", cursor: "pointer", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", display: "inline-flex", alignItems: "center", gap: 4 }}
                data-testid="login-mfa-back"
              >
                <ArrowLeft size={12} /> {t("login.back")}
              </button>
              <button
                type="button"
                onClick={() => { setUseBackupCode((v) => !v); setMfaCode(""); }}
                style={{ background: "transparent", border: 0, color: "#9a7530", cursor: "pointer", fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}
                data-testid="login-mfa-toggle-backup"
              >
                {useBackupCode
                  ? t("login.use_authenticator")
                  : t("login.use_backup_code")}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-5" style={{ marginTop: step === "credentials" ? 0 : "0.5rem" }}>
          <div className="pt-3 flex flex-col items-center">
            <button
              type="button"
              onClick={() => setShowSupport((v) => !v)}
              data-testid="login-support-toggle"
              style={{
                background: "transparent",
                border: 0,
                color: "#9a7530",
                fontFamily: "Cinzel, serif",
                fontSize: "0.78rem",
                letterSpacing: "0.22em",
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              {t("login.support")}
            </button>

            {showSupport && (
              <div
                data-testid="login-support-panel"
                style={{
                  marginTop: "10px",
                  width: "100%",
                  border: "1px solid rgba(154, 117, 48, 0.35)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  background: "rgba(245, 240, 232, 0.55)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="login-support-email"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#0d1b2a",
                    textDecoration: "none",
                    fontFamily: "Crimson Text, serif",
                    fontSize: "0.95rem",
                  }}
                >
                  <Mail size={16} color="#9a7530" />
                  <span>{SUPPORT_EMAIL}</span>
                </a>
                <a
                  href={`tel:${SUPPORT_PHONE}`}
                  data-testid="login-support-phone"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#0d1b2a",
                    textDecoration: "none",
                    fontFamily: "Crimson Text, serif",
                    fontSize: "0.95rem",
                  }}
                >
                  <Phone size={16} color="#9a7530" />
                  <span>{SUPPORT_PHONE}</span>
                </a>
              </div>
            )}

            {adminHost && (
              <button
                type="button"
                onClick={() => setShowSelfMfaReset((v) => !v)}
                data-testid="login-lost-mfa-toggle"
                style={{
                  background: "transparent",
                  border: 0,
                  color: "#9a7530",
                  fontFamily: "Cinzel, serif",
                  fontSize: "0.72rem",
                  letterSpacing: "0.18em",
                  cursor: "pointer",
                  padding: "10px 8px 0",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <ShieldOff size={12} /> {t("login.lost_authenticator")}
              </button>
            )}
          </div>
        </div>
      </form>

      {showSelfMfaReset && (
        <div className="dialog-overlay" onClick={() => setShowSelfMfaReset(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSelfMfaReset}
            style={{ maxWidth: 480 }}
            data-testid="self-mfa-reset-form"
          >
            <h2 className="dialog-title">
              {t("login.reset_superadmin_mfa")}
            </h2>
            <p className="kv-label" style={{ fontSize: "0.7rem", marginBottom: "0.8rem" }}>
              {t("login.this_is_the_secure_backdoor")}
            </p>
            <div>
              <label className="label-lodge">
                {t("login.environment_master_password")}
              </label>
              <input
                type="password"
                className="input-lodge"
                value={selfMfaPwd}
                onChange={(e) => setSelfMfaPwd(e.target.value)}
                required
                data-testid="self-mfa-pwd-input"
                autoComplete="off"
              />
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" className="btn-outline-gold" onClick={() => setShowSelfMfaReset(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="btn-primary-gold"
                disabled={selfMfaSubmitting || !selfMfaPwd.trim()}
                data-testid="self-mfa-submit"
              >
                {selfMfaSubmitting ? "..." : t("login.reset_mfa")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
